/**
 * @file Route-scoped Firestore listeners for the standalone Recalls view.
 */

import {
    collection,
    limit,
    onSnapshot,
    orderBy,
    query,
    where,
} from "https://www.gstatic.com/firebasejs/12.19.0/firebase-firestore.js";

import { db } from "../../app/firebase/firebase.js";
import {
    hasLoadedRecallsMonth,
    recallsRouteState,
    resetRecallsRouteState,
    setCachedOperationalRecalls,
    setCachedRecallDrafts,
    setRecallsMonthBoundary,
    setRecallsMonthLoadState,
    setRecallsMonthRows,
} from "./recallsState.js";
import { closeRecallNotesHistoryModal } from "./recallsNotesModal.js";
import { closeRecallMobileReadModal } from "./recallsMobileReadModal.js";
import {
    buildOperationalRecallsQuery,
    fetchLatestRecallBoundaryMonth,
    fetchOldestRecallBoundaryMonth,
    fetchRecallsMonthRows,
    isValidRecallMonthKey,
} from "./recallsMonthQueries.js";

const RECALL_DRAFT_LIMIT = 200;

let activeRouteListeners = [];
let activeListenerKey = null;
const activeMonthBucketListeners = new Map();

function mapSnapshotDocs(snapshot) {
    return snapshot.docs.map((snapshotDoc) => ({
        id: snapshotDoc.id,
        ...snapshotDoc.data(),
    }));
}

function notifyDataChanged(callback) {
    if (typeof callback === "function") {
        callback();
    }
}

function detachRecallsMonthBucketListener(monthKey) {
    const listeners = activeMonthBucketListeners.get(monthKey) || [];
    listeners.forEach((unsubscribe) => {
        try {
            unsubscribe();
        } catch (error) {
            console.warn("Error detaching Recalls month listener:", error);
        }
    });
    activeMonthBucketListeners.delete(monthKey);
    setRecallsMonthLoadState(monthKey);
}

/**
 * Detaches live Recalls month-bucket listeners.
 * @returns {void}
 */
export function detachRecallsMonthBucketListeners() {
    Array.from(activeMonthBucketListeners.keys())
        .forEach(detachRecallsMonthBucketListener);
}

function attachLiveRecallsMonthBucket(monthKey, { force = false, onData } = {}) {
    if (!isValidRecallMonthKey(monthKey)) {return;}
    if (activeMonthBucketListeners.has(monthKey)) {
        if (force !== true) {return;}
        detachRecallsMonthBucketListener(monthKey);
    }

    setRecallsMonthLoadState(monthKey, { loading: true });
    notifyDataChanged(onData);

    const activeRowsQuery = query(
        collection(db, "recalls"),
        where("isClosedResolved", "==", false),
        where("effectiveMonth", "==", monthKey),
        orderBy("recall_date", "asc"),
    );
    const closedRowsQuery = query(
        collection(db, "recalls"),
        where("isClosedResolved", "==", true),
        where("closedMonth", "==", monthKey),
        orderBy("closedAt", "desc"),
    );
    const listeners = [
        onSnapshot(
            activeRowsQuery,
            (snapshot) => {
                setRecallsMonthRows(monthKey, {
                    activeRows: mapSnapshotDocs(snapshot),
                }, {
                    activeLoaded: true,
                    closedLoaded: false,
                });
                notifyDataChanged(onData);
            },
            (error) => {
                console.error(`Error listening to active Recalls month ${monthKey}:`, error);
                setRecallsMonthLoadState(monthKey, { error });
                notifyDataChanged(onData);
            },
        ),
        onSnapshot(
            closedRowsQuery,
            (snapshot) => {
                setRecallsMonthRows(monthKey, {
                    closedRows: mapSnapshotDocs(snapshot),
                }, {
                    activeLoaded: false,
                    closedLoaded: true,
                });
                notifyDataChanged(onData);
            },
            (error) => {
                console.error(`Error listening to closed Recalls month ${monthKey}:`, error);
                setRecallsMonthLoadState(monthKey, { error });
                notifyDataChanged(onData);
            },
        ),
    ];
    activeMonthBucketListeners.set(monthKey, listeners);
}

async function loadRecallsBoundaries({ onData, showPreviousMonths = false } = {}) {
    try {
        const latestMonth = await fetchLatestRecallBoundaryMonth();
        setRecallsMonthBoundary("latest", latestMonth);
        notifyDataChanged(onData);
    } catch (error) {
        console.error("Error loading latest Recalls month boundary:", error);
        recallsRouteState.listenerStatus = "error";
        recallsRouteState.listenerError = error;
        notifyDataChanged(onData);
    }

    if (showPreviousMonths !== true) {return;}

    try {
        const oldestMonth = await fetchOldestRecallBoundaryMonth();
        setRecallsMonthBoundary("oldest", oldestMonth);
        notifyDataChanged(onData);
    } catch (error) {
        console.error("Error loading oldest Recalls month boundary:", error);
        recallsRouteState.listenerStatus = "error";
        recallsRouteState.listenerError = error;
        notifyDataChanged(onData);
    }
}

/**
 * Loads a month bucket on demand and caches it for the route session.
 * @param {string} monthKey Month key in YYYY-MM form.
 * @param {object} [options] Fetch options.
 * @param {boolean} [options.includeActive=true] Whether active rows are needed.
 * @param {boolean} [options.force=false] Whether to refresh even when cached.
 * @param {boolean} [options.showClosed=false] Whether closed rows are needed.
 * @param {Function} [options.onData] Callback when data changes.
 * @returns {Promise<void>}
 */
export async function loadRecallsMonthBucket(
    monthKey,
    { force = false, includeActive = true, onData, showClosed = false } = {},
) {
    const includeClosed = showClosed === true;
    if (includeClosed) {
        attachLiveRecallsMonthBucket(monthKey, { force, onData });
        return;
    }

    if (
        force !== true &&
        hasLoadedRecallsMonth(monthKey, { includeActive, includeClosed })
    ) {
        return;
    }

    setRecallsMonthLoadState(monthKey, { loading: true });
    notifyDataChanged(onData);

    try {
        const rows = await fetchRecallsMonthRows(monthKey, {
            includeActive,
            includeClosed,
        });
        setRecallsMonthRows(monthKey, rows, {
            activeLoaded: includeActive,
            closedLoaded: includeClosed,
        });
        notifyDataChanged(onData);
    } catch (error) {
        console.error(`Error loading Recalls month ${monthKey}:`, error);
        setRecallsMonthLoadState(monthKey, { error });
        notifyDataChanged(onData);
    }
}

/**
 * Detaches Recalls route listeners and clears route-local data.
 * @returns {void}
 */
export function detachRecallsRouteListeners() {
    closeRecallNotesHistoryModal();
    closeRecallMobileReadModal();
    detachRecallsMonthBucketListeners();
    activeRouteListeners.forEach((unsubscribe) => {
        try {
            unsubscribe();
        } catch (error) {
            console.warn("Error detaching Recalls listener:", error);
        }
    });
    activeRouteListeners = [];
    activeListenerKey = null;
    resetRecallsRouteState();
}

function detachCurrentRouteListeners() {
    detachRecallsMonthBucketListeners();
    activeRouteListeners.forEach((unsubscribe) => {
        try {
            unsubscribe();
        } catch (error) {
            console.warn("Error replacing Recalls listener:", error);
        }
    });
    activeRouteListeners = [];
    activeListenerKey = null;
}

/**
 * Attaches Recalls listeners for the active route.
 * @param {object} [options] Listener options.
 * @param {boolean} [options.showClosed=false] Whether closed rows are loaded.
 * @param {Function} [options.onData] Callback when listener data changes.
 * @returns {void}
 */
export function attachRecallsRouteListeners({
    onData,
    showClosed = false,
    showPreviousMonths = false,
} = {}) {
    const nextListenerKey = "recalls-route";

    if (showClosed !== true) {
        recallsRouteState.showClosed = false;
        detachRecallsMonthBucketListeners();
    }

    if (activeListenerKey === nextListenerKey) {
        if (showPreviousMonths === true && !recallsRouteState.oldestKnownMonthBoundary) {
            void loadRecallsBoundaries({ onData, showPreviousMonths });
        }
        return;
    }

    detachCurrentRouteListeners();

    recallsRouteState.listenerStatus = "loading";
    recallsRouteState.listenerError = null;
    recallsRouteState.loadedThroughMonth = null;
    activeListenerKey = nextListenerKey;

    const activeRecallsQuery = buildOperationalRecallsQuery();

    const activeRecallsListener = onSnapshot(
        activeRecallsQuery,
        (snapshot) => {
            setCachedOperationalRecalls(mapSnapshotDocs(snapshot));
            recallsRouteState.listenerStatus = "ready";
            recallsRouteState.listenerError = null;
            notifyDataChanged(onData);
        },
        (error) => {
            console.error("Error listening to active Recalls:", error);
            setCachedOperationalRecalls([]);
            recallsRouteState.listenerStatus = "error";
            recallsRouteState.listenerError = error;
            notifyDataChanged(onData);
        },
    );
    activeRouteListeners.push(activeRecallsListener);

    const draftsQuery = query(
        collection(db, "recall_drafts"),
        orderBy("updatedAt", "desc"),
        limit(RECALL_DRAFT_LIMIT),
    );

    const draftsListener = onSnapshot(
        draftsQuery,
        (snapshot) => {
            setCachedRecallDrafts(mapSnapshotDocs(snapshot));
            notifyDataChanged(onData);
        },
        (error) => {
            console.error("Error listening to Recalls drafts:", error);
            setCachedRecallDrafts([]);
            recallsRouteState.listenerStatus = "error";
            recallsRouteState.listenerError = error;
            notifyDataChanged(onData);
        },
    );
    activeRouteListeners.push(draftsListener);

    void loadRecallsBoundaries({ onData, showPreviousMonths });
}
